import { createClient } from '@supabase/supabase-js'
import TVDashboard from './TVDashboard';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import './index.css';
import './styles/designSystem.css';
import emailjs from '@emailjs/browser';

import ComingSoon from './pages/ComingSoon'

import ITSMDashboard from './pages/itsm/ITSMDashboard'
import ServiceCatalog from './pages/itsm/ServiceCatalog'
import ITSMTickets from './pages/itsm/ITSMTickets'
import ITSMTicketDetail from './pages/itsm/ITSMTicketDetail'
import ITSMAgents from './pages/itsm/ITSMAgents'
import ITSMTeams from './pages/itsm/ITSMTeams'
import ITSMVendors from './pages/itsm/ITSMVendors'
import PurchaseOrders from './pages/itsm/PurchaseOrders'
import PODetail from './pages/itsm/PODetail'
import ITSMSettings from './pages/itsm/ITSMSettings'

import Assets from './pages/itsm/Assets'
import Changes from './pages/itsm/Changes'
import Problems from './pages/itsm/Problems'
import KnowledgeBase from './pages/itsm/KnowledgeBase'
import ITSMReports from './pages/itsm/ITSMReports'
import { loadCurrentStaffUser, canViewSection } from './lib/auth/roles'
import { PhotoModalProvider } from './contexts/PhotoModalContext'




emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY!);

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
)

supabase.auth.onAuthStateChange((event, _session) => {
  if (event === 'PASSWORD_RECOVERY') {
    window.location.href = '/client/reset-password'
  }
})
// Previously this only checked "does a session exist" — no role check at
// all gated any staff route (including every /dashboard/itsm/* page). It
// now loads the staff user_profiles row and, when a `section` is given,
// checks canViewSection against it — the same PERMISSIONS table App.jsx
// uses (src/lib/auth/roles.ts), so the route guard and the in-app menu
// can never disagree about who can see what. `requireItsmAdmin` adds a
// second check against itsm_agents.role for the ITSM-admin-only pages
// (agents/teams/vendors/settings/PO approval).
function ProtectedDashboard({ children, section, requireItsmAdmin }: { children: React.ReactNode, section?: string, requireItsmAdmin?: boolean }) {
  const [status, setStatus] = React.useState<'loading' | 'allowed' | 'denied'>('loading')

  React.useEffect(() => {
    let cancelled = false

    const check = async () => {
      const staffUser = await loadCurrentStaffUser(supabase)
      if (cancelled) return

      if (!staffUser) {
        // No session, or a session that isn't a staff account (e.g. a
        // client session that navigated here directly) — send to the
        // client portal rather than rendering a staff page for them.
        const { data: { session } } = await supabase.auth.getSession()
        window.location.href = session ? '/client' : '/'
        return
      }

      if (section && !canViewSection(staffUser.position, section)) {
        window.location.href = '/'
        return
      }

      if (requireItsmAdmin) {
        const { data: agentRow } = await supabase
          .from('itsm_agents')
          .select('role, is_active')
          .eq('user_id', staffUser.user_id)
          .maybeSingle()
        if (cancelled) return
        if (!agentRow || agentRow.is_active === false || agentRow.role !== 'admin') {
          window.location.href = '/dashboard/itsm'
          return
        }
      }

      setStatus('allowed')
    }

    check()
    return () => { cancelled = true }
  }, [section, requireItsmAdmin])

  if (status !== 'allowed') return null
  return <>{children}</>
}


function AppRoutes() {
  return (
    <Routes>
      {/* Client Support portal (/client/*) — paused on Bakri's instruction
          (2026-10-08) in favor of focusing on the main dashboard + the real
          Client Portal (client.ritisbase.com). A single splat route covers
          every sub-path (login, register, tickets, reset-password, etc.)
          with a "Coming Soon" placeholder. To bring it back later, delete
          this Route and restore the specific /client/* routes + imports
          from git history — none of the underlying pages were touched. */}
      <Route path="/client/*" element={<ComingSoon />} />

      {/* Dashboard — legacy Support/Clients/Calendar/Reports pages (the old
          `tickets` table) are retired (Phase 9) in favor of the ITSM admin
          dashboard. /support and /reports have direct ITSM equivalents and
          redirect; /clients and /calendar have no one-to-one replacement
          (client records live in the Accounts/HR portals, and visits are
          shown per-ticket rather than on a standalone calendar) so those
          paths are simply removed. */}
      <Route path="/dashboard/support" element={<Navigate to="/dashboard/itsm/tickets" replace />} />
      <Route path="/dashboard/support/:id" element={<Navigate to="/dashboard/itsm/tickets" replace />} />
      <Route path="/dashboard/reports" element={<Navigate to="/dashboard/itsm/reports" replace />} />

      {/* TV */}
      <Route path="/tv" element={<TVDashboard />} />
      <Route path="/dashboard/itsm" element={<ProtectedDashboard section="ITSM"><ITSMDashboard /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/catalog" element={<ProtectedDashboard section="ITSM"><ServiceCatalog /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/tickets" element={<ProtectedDashboard section="ITSM"><ITSMTickets /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/tickets/:id" element={<ProtectedDashboard section="ITSM"><ITSMTicketDetail /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/agents" element={<ProtectedDashboard section="ITSM" requireItsmAdmin><ITSMAgents /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/teams" element={<ProtectedDashboard section="ITSM" requireItsmAdmin><ITSMTeams /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/vendors" element={<ProtectedDashboard section="ITSM" requireItsmAdmin><ITSMVendors /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/po" element={<ProtectedDashboard section="ITSM"><PurchaseOrders /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/po/:id" element={<ProtectedDashboard section="ITSM" requireItsmAdmin><PODetail /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/settings" element={<ProtectedDashboard section="ITSM" requireItsmAdmin><ITSMSettings /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/assets" element={<ProtectedDashboard section="ITSM"><Assets /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/changes" element={<ProtectedDashboard section="ITSM"><Changes /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/problems" element={<ProtectedDashboard section="ITSM"><Problems /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/kb" element={<ProtectedDashboard section="ITSM"><KnowledgeBase /></ProtectedDashboard>} />
      <Route path="/dashboard/itsm/reports" element={<ProtectedDashboard section="ITSM"><ITSMReports /></ProtectedDashboard>} />

      {/* Sales — Showcase page paused too (same instruction as /client/* above). */}
      <Route path="/showcase" element={<ComingSoon />} />
      {/* Main App */}
      {/* ✅ PhotoModalProvider يلفّ App من هنا، مش جواها — عشان فتح/قفل صورة
          (setModalPhoto) يعمل re-render للـ Provider ده بس، مش لـ App كلها
          (الهيدر + أي صفحة تقيلة فاتحة). الـ <App/> element نفسه ما بيتغيّرش
          هنا بين كل render للـ Provider، فـ React بيعمل bail-out ومايعيدش
          رندر App خالص. */}
      <Route path="/*" element={<PhotoModalProvider><App /></PhotoModalProvider>} />
    </Routes>
  )
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
)

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </React.StrictMode>
)